/**
 * I wanted to do some fun stuff with drawing lines between dots, which is simple enough
 * when working in screen coordinates. But I'm a funny person and I wanted to throw in more complexity
 * with scaling and panning, and it turned out that it's not very easy to do.
 *
 * After a lot of experimentation and time (somewhat) wasted, I've come to realise that to make life simpler (or not),
 * I need to actually write a proper coordinate system.
 *
 * This library provides basic world coordinate functionality for the ExperiencesShowcase component.
 * It'll handle the coordinate manipulation when zooming and panning.
 */

import { dist2 } from "./mathUtils";

/**
 * Type definitions
 */
// For our dearest 2D vector
export type Vec2 = { x: number; y: number };

// The point on a line (either start or end)
type LinePoint = {
  id: string;
  position: Vec2;
  nodeType: NodeType;
};

export type Line = {
  start: LinePoint;
  end: LinePoint;
};

export type Size = { width: number | string; height: number | string };

export type NodeType = "showcase" | "spawned";

// A node in world space (we think in terms of world coordinates instead of screen coordinates)
export type WorldNode = {
  id: string;
  position: Vec2; // world coordinates
  kind: NodeType; // different nodes are handled differently
  team: "user" | "dev"; // for highlighting
  size?: Size;
  src?: string;
};

// Our viewport/camera into world space
// We store it this way so we can easily manipulate all our nodes relative to the camera
export type Camera = {
  zoom: number;
  offset: Vec2; // for panning later
  viewportSize: Vec2;
};

export const TEAM_COLOURS = {
  USER: "rgb(59, 130, 246)", // theme primary colour
  DEV: "rgb(168, 85, 247)", // theme secondary colour
};

/**
 * Scales a dimension (number or string with unit) based on zoom level
 * @param dim The dimension to scale
 * @param zoom The zoom level
 * @returns The scaled dimension
 */
export const scaleDimension = (dim: number | string, zoom: number): number | string => {
  let result: number | string = dim;

  if (typeof dim === "number") {
    result = dim * zoom;
  } else if (typeof dim === "string") {
    // Split into number and unit
    const match = dim.match(/^([\d.]+)([a-z%]+)$/); // e.g., "20svh" -> ["20svh", "20", "svh"]
    if (match) {
      const value = parseFloat(match[1] || "0");
      const unit = match[2] || "";
      result = value * zoom + unit;
    }
  }

  return result;
};

/**
 * Scales a Size object based on zoom level
 * @param size The size to scale
 * @param zoom The zoom level
 * @returns The scaled size
 */
export const scaleSize = (size: Size, zoom: number): Size => {
  return {
    width: scaleDimension(size.width, zoom),
    height: scaleDimension(size.height, zoom),
  };
};

/**
 * Transforms world coordinates into screen coordinates based on the camera's zoom and offset.
 * @param node The node to render
 * @param camera The current camera/view settings
 * @returns The position of the node in screen coordinates
 */
export const render = (node: WorldNode, camera: Camera, override?: Partial<Size>): Vec2 & Partial<Size> => {
  // Scale y coordinates based on viewport size
  // Only scale the x coordinates if the node is a showcase node
  let x = node.kind === "showcase" ? (node.position.x / 1920) * camera.viewportSize.x : node.position.x;
  let y = (node.position.y / 1080) * camera.viewportSize.y;

  // Apply zoom and offset to convert world coordinates to screen coordinates
  x = x * camera.zoom + camera.offset.x;
  y = y * camera.zoom + camera.offset.y;

  // Apply zoom offset to height and width if specified
  let { height, width } = override || node.size || {};
  if (height && width) {
    ({ height, width } = scaleSize({ height, width }, camera.zoom));
  }

  return { x, y, height, width };
};

// Helper to get center position of WorldNode
const getCenter = (node: WorldNode): Vec2 | null => {
  if (!node.size) return null;
  const { width, height } = node.size;
  if (typeof width === "number" && typeof height === "number") {
    return {
      x: node.position.x + width / 2,
      y: node.position.y + height / 2,
    };
  }
  return null;
};

/**
 * Draws lines between nodes ensuring each node has at least four connections.
 * Uses a highly efficient MST (Prim's algorithm with a min-heap), then adds extra edges as needed.
 * @param nodes The nodes to draw lines between
 * @returns Coordinates of the lines to draw
 */
export const drawLines = (nodes: WorldNode[]): Line[] => {
  // Ensure we have at least 2 nodes
  const nodeCount = nodes.length;
  if (nodeCount < 2) return [];

  //-- Prim's MST algorithm --//
  const includedInMST = Array(nodeCount).fill(false);
  const minEdgeWeight = Array(nodeCount).fill(Infinity);
  const mstParent = Array(nodeCount).fill(-1);
  minEdgeWeight[0] = 0;

  // Build MST
  for (let step = 0; step < nodeCount; step++) {
    // Find node with minimal edge weight not yet in MST
    let minNodeIdx = -1;
    for (let idx = 0; idx < nodeCount; idx++) {
      if (!includedInMST[idx] && (minNodeIdx === -1 || minEdgeWeight[idx] < minEdgeWeight[minNodeIdx])) {
        minNodeIdx = idx;
      }
    }

    // All nodes included
    if (minNodeIdx === -1) break;

    // Mark node as included
    includedInMST[minNodeIdx] = true;

    // Update edge weights for neighbors
    for (let neighborIdx = 0; neighborIdx < nodeCount; neighborIdx++) {
      // Skip if already included
      if (includedInMST[neighborIdx]) continue;

      // Obtain nodes
      const minNode = nodes[minNodeIdx];
      const neighborNode = nodes[neighborIdx];

      if (!minNode || !neighborNode) continue;

      // Calculate squared distance and update edge weight if smaller
      const distance = dist2(minNode.position, neighborNode.position);
      if (distance < minEdgeWeight[neighborIdx]) {
        minEdgeWeight[neighborIdx] = distance;
        mstParent[neighborIdx] = minNodeIdx;
      }
    }
  }

  // Build edge set from MST
  const edgeSet = new Set<number>();
  const nodeDegree = Array(nodeCount).fill(0);

  const getEdgeKey = (a: number, b: number) => (a < b ? a * nodeCount + b : b * nodeCount + a);

  // Add MST edges for each node
  for (let childIdx = 1; childIdx < nodeCount; childIdx++) {
    const parentIdx = mstParent[childIdx];
    if (parentIdx === -1) continue;
    const key = getEdgeKey(parentIdx, childIdx);
    edgeSet.add(key);
    nodeDegree[parentIdx]++;
    nodeDegree[childIdx]++;
  }

  // Ensure each node has at least 4 connections by adding closest edges
  for (let nodeIdx = 0; nodeIdx < nodeCount; nodeIdx++) {
    while (nodeDegree[nodeIdx] < 4) {
      // Find closest neighbor not already connected
      let closestNeighborIdx = -1;
      let closestDistance = Infinity;

      // Iterate through all other nodes to find the closest
      for (let neighborIdx = 0; neighborIdx < nodeCount; neighborIdx++) {
        // Skip self and already connected nodes
        if (neighborIdx === nodeIdx) continue;
        const key = getEdgeKey(nodeIdx, neighborIdx);
        if (edgeSet.has(key)) continue;

        // Get nodes and skip if undefined
        const currNode = nodes[nodeIdx];
        const neighborNode = nodes[neighborIdx];
        if (!currNode || !neighborNode) continue;

        // Calculate distance and check if it's the closest
        const distance = dist2(currNode.position, neighborNode.position);
        if (distance < closestDistance) {
          // Update closest
          closestDistance = distance;
          closestNeighborIdx = neighborIdx;
        }
      }
      if (closestNeighborIdx === -1) break;
      const key = getEdgeKey(nodeIdx, closestNeighborIdx);
      edgeSet.add(key);
      nodeDegree[nodeIdx]++;
      nodeDegree[closestNeighborIdx]++;
    }
  }

  // Convert numeric edges to Line objects
  const lines: Line[] = [];
  edgeSet.forEach((key) => {
    // Decode node indices from key
    const nodeAIdx = Math.floor(key / nodeCount);
    const nodeBIdx = key % nodeCount;
    const nodeA = nodes[nodeAIdx]!;
    const nodeB = nodes[nodeBIdx]!;

    // Add line
    lines.push({
      start: {
        id: nodeA.id,
        position: nodeA.position,
        nodeType: nodeA.kind,
      },
      end: {
        id: nodeB.id,
        position: nodeB.position,
        nodeType: nodeB.kind,
      },
    });
  });

  return lines;
};

/**
 * Transforms a Line's start and end positions into screen coordinates based on the camera's zoom and offset.
 * @param line The line to render
 * @param camera The current camera/view settings
 * @returns The start and end positions of the line in screen coordinates
 */
export const renderLine = (line: Line, camera: Camera): { start: Vec2; end: Vec2 } => {
  const transform = (linePoint: LinePoint): Vec2 => {
    let x = linePoint.nodeType === "showcase" ? (linePoint.position.x / 1920) * camera.viewportSize.x : linePoint.position.x;
    let y = (linePoint.position.y / 1080) * camera.viewportSize.y;
    x = x * camera.zoom + camera.offset.x;
    y = y * camera.zoom + camera.offset.y;
    return { x, y };
  };

  return {
    start: transform(line.start),
    end: transform(line.end),
  };
};
